'use client';

import {
  type AiResponse,
  AiResponseReport,
  AiResponseReportFile,
  type AiResponseReportFileWithUrl,
  BatchUpdateAiResponseReportFilesRequest,
} from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  LoadingIcon,
  MultipleSelect,
  Skeleton,
  Textarea,
  toast,
} from '@kalos/ui';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { ImageIcon } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { queryKeys } from '../../../../hooks/react-query/constants';
import {
  useBatchGetAiResponseReportQuery,
  useBatchGetAiResponsesSystemQuery,
  useBatchUpdateAiResponseReportFilesMutation,
  useGetAiResponseReportDataQuery,
  useUpdateAiResponseReportMutation,
} from '../../../../hooks/react-query/useAiResponseReportQuery';
import { useEventBatchGetQuery } from '../../../../hooks/react-query/useEventsQuery';
import { useMemoQuery } from '../../../../hooks/react-query/useMemoQuery';
import { useS3FileUrlQuery } from '../../../../hooks/react-query/useS3';
import { useMobileDevice } from '../../../../hooks/useMedia';
import { OpenAIClientService } from '../../../../tools/helpers';

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i;
const DOWNLOAD_COOLDOWN_MS = 2000;

function sanitizeFilenameSegment(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return 'Unknown';
  return trimmed
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, '_')
    .slice(0, 120);
}

/** Local download name: Kalos_ProjectUpdate_<Job Number>_<Date of Download>.pdf */
function buildProjectUpdatePdfFilename(jobNumberPart: string, downloadDate: Date): string {
  const dateStr = format(downloadDate, 'yyyy-MM-dd');
  return `Kalos_ProjectUpdate_${sanitizeFilenameSegment(jobNumberPart)}_${dateStr}.pdf`;
}

function getFilesToUpdate(
  files: AiResponseReportFileWithUrl[],
  selectedFileIds: number[],
  reportId: number,
): AiResponseReportFile[] {
  const selectedSet = new Set(selectedFileIds);
  const updates: AiResponseReportFile[] = [];

  for (const file of files) {
    if (!file.id) continue;
    const desiredSelected = selectedSet.has(file.id);
    const desiredOrder = desiredSelected ? selectedFileIds.indexOf(file.id) : 0;
    const currentSelected = file.isSelected ?? false;
    const currentOrder = file.displayOrder ?? 0;

    if (currentSelected !== desiredSelected || currentOrder !== desiredOrder) {
      updates.push(
        AiResponseReportFile.create({
          id: file.id,
          aiResponseReportId: reportId,
          isSelected: desiredSelected,
          displayOrder: desiredOrder,
          fieldMask: ['ai_response_report_id', 'is_selected', 'display_order'],
        }),
      );
    }
  }
  return updates;
}

function isImage(file: { fileName?: string; expiringUrl?: string }): boolean {
  const name = file.fileName ?? '';
  if (name) {
    return IMAGE_EXTENSIONS.test(name);
  }
  return !!file.expiringUrl;
}

interface BuildPdfModalProps {
  aiResponse: AiResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPdfBuilt?: () => void;
  /** The project's prime job, whose job number is preselected for the PDF. */
  primaryEventId?: number;
}

/** One photo to pick for the PDF, loaded from a fresh signed URL (the stored one expires). */
const PdfPhotoTile = ({
  file,
  order,
  onToggle,
}: {
  file: AiResponseReportFileWithUrl;
  /** Its place in the PDF, from 1; 0 when not picked. */
  order: number;
  onToggle: () => void;
}) => {
  const urlQuery = useS3FileUrlQuery({
    filter: { fileName: file.fileName, bucket: file.fileBucket },
    enabled: !!file.fileName && !!file.fileBucket,
  });
  const [hasFailed, setHasFailed] = useState(false);
  const src = urlQuery.data || file.expiringUrl;
  const isSelected = order > 0;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={isSelected}
      aria-label={
        isSelected ? `Photo ${order} in the PDF: ${file.fileName}` : `Add photo ${file.fileName}`
      }
      className={`bg-muted relative flex aspect-square items-center justify-center overflow-hidden rounded-md border-2 ${
        isSelected ? 'border-primary' : 'border-transparent'
      }`}
    >
      {urlQuery.isPending && file.fileBucket ? (
        <Skeleton className="h-full w-full rounded-none" />
      ) : src && !hasFailed ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setHasFailed(true)}
        />
      ) : (
        <ImageIcon className="text-muted-foreground h-6 w-6" aria-hidden />
      )}
      {isSelected && (
        <span className="bg-primary text-primary-foreground absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums">
          {order}
        </span>
      )}
    </button>
  );
};

export const BuildPdfModal = ({
  aiResponse,
  open,
  onOpenChange,
  onPdfBuilt,
  primaryEventId,
}: BuildPdfModalProps) => {
  const isMobileDevice = useMobileDevice();
  const hasPreselectedJobNumber = useRef(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [editedText, setEditedText] = useState('');
  const [reportId, setReportId] = useState<number | null>(null);
  const [textSaveStatus, setTextSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [blobPdfUrl, setBlobPdfUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  /** True while fetching PDF bytes for preview/blob URL (after server returns pdfUrl) */
  const [isFetchingPdfBlob, setIsFetchingPdfBlob] = useState(false);
  const [pdfGenerationError, setPdfGenerationError] = useState<string | null>(null);
  const [isProcessingStep1, setIsProcessingStep1] = useState(false);
  const [reportTextHydratedForId, setReportTextHydratedForId] = useState<number | null>(null);
  const hasInitializedFromDbRef = useRef(false);
  const onPdfBuiltRef = useRef(onPdfBuilt);
  onPdfBuiltRef.current = onPdfBuilt;
  const lastDownloadTimeRef = useRef(0);
  const [isDownloadCooldown, setIsDownloadCooldown] = useState(false);
  const [selectedJobNumbers, setSelectedJobNumbers] = useState<string[]>([]);
  const [jobNumberFieldError, setJobNumberFieldError] = useState<string | null>(null);
  const jobNumbersInputRef = useRef<HTMLInputElement>(null);
  const jobNumbersErrorId = useId();

  const aiResponsesId = aiResponse.id ?? 0;

  const aiResponsesSystemQuery = useBatchGetAiResponsesSystemQuery({
    aiResponsesId,
    enabled: open && aiResponsesId > 0,
  });
  const memoIds = useMemo(() => {
    const results = aiResponsesSystemQuery.data?.results ?? [];
    return results.map((r) => r.memoId).filter((id) => id > 0);
  }, [aiResponsesSystemQuery.data?.results]);

  const memosQuery = useMemoQuery({
    filters: { idList: memoIds.join(',') },
    enabled: open && memoIds.length > 0,
  });

  const eventIds = useMemo(() => {
    const memos = memosQuery.data?.results ?? [];
    const ids = new Set<number>();
    for (const m of memos) {
      const eid = m.eventId ?? 0;
      if (eid > 0) ids.add(eid);
    }
    // The prime job too, so its job number can be preselected even if it has no notes here.
    if (primaryEventId && primaryEventId > 0) ids.add(primaryEventId);
    return Array.from(ids);
  }, [memosQuery.data?.results, primaryEventId]);

  const eventsQuery = useEventBatchGetQuery({
    filters: { idList: eventIds.join(',') },
    enabled: open && eventIds.length > 0,
  });

  const jobNumberOptions = useMemo(() => {
    const events = eventsQuery.data?.results ?? [];
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const e of events) {
      const jn = e.logJobNumber ?? '';
      if (jn && !seen.has(jn)) {
        seen.add(jn);
        options.push({ value: jn, label: jn });
      }
    }
    return options;
  }, [eventsQuery.data?.results]);

  // Preselect the prime job's number (or the only job's) once the options load, so most PDFs
  // need no picking. Removing it is still possible.
  useEffect(() => {
    if (hasPreselectedJobNumber.current || jobNumberOptions.length === 0) return;
    const primeJobNumber = eventsQuery.data?.results?.find(
      (event) => event.id === primaryEventId,
    )?.logJobNumber;
    const preselected =
      primeJobNumber || (jobNumberOptions.length === 1 ? jobNumberOptions[0].value : '');
    hasPreselectedJobNumber.current = true;
    if (preselected) setSelectedJobNumbers((current) => (current.length ? current : [preselected]));
  }, [jobNumberOptions, eventsQuery.data?.results, primaryEventId]);

  const getPdfDownloadFilename = useCallback(() => {
    const jobNumberRaw =
      selectedJobNumbers.length > 0
        ? selectedJobNumbers.join('-')
        : jobNumberOptions.length === 1
          ? jobNumberOptions[0].value
          : '';
    return buildProjectUpdatePdfFilename(jobNumberRaw || 'Unknown', new Date());
  }, [selectedJobNumbers, jobNumberOptions]);

  const isJobNumbersPipelineLoading = useMemo(() => {
    if (step !== 2) return false;
    if (
      aiResponsesId > 0 &&
      (aiResponsesSystemQuery.isLoading || aiResponsesSystemQuery.isFetching)
    ) {
      return true;
    }
    if (memoIds.length > 0 && (memosQuery.isLoading || memosQuery.isFetching)) {
      return true;
    }
    if (eventIds.length > 0 && (eventsQuery.isLoading || eventsQuery.isFetching)) {
      return true;
    }
    return false;
  }, [
    step,
    aiResponsesId,
    memoIds.length,
    eventIds.length,
    aiResponsesSystemQuery.isLoading,
    aiResponsesSystemQuery.isFetching,
    memosQuery.isLoading,
    memosQuery.isFetching,
    eventsQuery.isLoading,
    eventsQuery.isFetching,
  ]);

  const queryClient = useQueryClient();

  const batchGetQuery = useBatchGetAiResponseReportQuery({
    aiResponsesId,
    enabled: open && aiResponsesId > 0,
  });

  const existingReportId = useMemo(() => {
    const results = batchGetQuery.data?.results;
    if (!results || results.length === 0) return null;
    return results[0]?.id ?? null;
  }, [batchGetQuery.data?.results]);

  const effectiveReportId = reportId ?? existingReportId;

  const reportDataQuery = useGetAiResponseReportDataQuery({
    reportId: effectiveReportId ?? 0,
    enabled: open && (effectiveReportId ?? 0) > 0,
  });

  const updateReportMutation = useUpdateAiResponseReportMutation();
  const batchUpdateReportFilesMutation = useBatchUpdateAiResponseReportFilesMutation();

  const files = useMemo(() => {
    const list = reportDataQuery.data?.files ?? [];
    const imageFiles = list.filter((f) => isImage(f));
    return imageFiles.sort((a, b) => {
      const aSel = a.isSelected ?? false;
      const bSel = b.isSelected ?? false;
      if (aSel !== bSel) return aSel ? -1 : 1;
      if (aSel && bSel) return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      return (a.id ?? 0) - (b.id ?? 0);
    });
  }, [reportDataQuery.data?.files]);

  const serverReportText = useMemo(() => {
    if (
      !effectiveReportId ||
      effectiveReportId <= 0 ||
      reportTextHydratedForId !== effectiveReportId
    ) {
      return null;
    }
    return reportDataQuery.data?.report?.text ?? '';
  }, [effectiveReportId, reportTextHydratedForId, reportDataQuery.data?.report?.text]);

  const isStep2ReportTextReady =
    !!effectiveReportId && effectiveReportId > 0 && reportTextHydratedForId === effectiveReportId;

  useEffect(() => {
    hasInitializedFromDbRef.current = false;
    setReportTextHydratedForId(null);
  }, [effectiveReportId]);

  useEffect(() => {
    if (!effectiveReportId || effectiveReportId <= 0) return;
    if (!reportDataQuery.isSuccess || !reportDataQuery.data?.report) return;
    if (reportTextHydratedForId === effectiveReportId) return;

    const text = reportDataQuery.data.report.text ?? '';
    setEditedText(text);
    setReportTextHydratedForId(effectiveReportId);
  }, [
    effectiveReportId,
    reportDataQuery.isSuccess,
    reportDataQuery.data?.report,
    reportTextHydratedForId,
  ]);

  useEffect(() => {
    if (!reportDataQuery.data?.files || hasInitializedFromDbRef.current) return;
    const imageFiles = reportDataQuery.data.files.filter((f) => isImage(f));
    const selected = imageFiles
      .filter((f) => f.isSelected)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((f) => f.id)
      .filter((id): id is number => id !== undefined && id > 0);
    if (selected.length > 0) {
      setSelectedFileIds(selected);
    }
    hasInitializedFromDbRef.current = true;
  }, [reportDataQuery.data?.files]);

  const debouncedSaveText = useMemo(
    () =>
      debounce(async (text: string, rid: number) => {
        setTextSaveStatus('saving');
        try {
          await updateReportMutation.mutateAsync(
            AiResponseReport.create({ id: rid, text, fieldMask: ['text'] }),
          );
          setTextSaveStatus('saved');
        } catch {
          setTextSaveStatus('idle');
          toast({
            title: 'Save failed',
            description: 'Could not save report text. Please try again.',
            variant: 'destructive',
          });
        }
      }, 1000),
    [updateReportMutation],
  );

  useEffect(() => {
    if (
      serverReportText === null ||
      editedText === serverReportText ||
      !effectiveReportId ||
      effectiveReportId <= 0 ||
      step !== 2
    ) {
      return () => debouncedSaveText.cancel();
    }
    debouncedSaveText(editedText, effectiveReportId);
    return () => debouncedSaveText.cancel();
  }, [editedText, serverReportText, effectiveReportId, step, debouncedSaveText]);

  useEffect(() => {
    if (!pdfUrl) {
      setBlobPdfUrl(null);
      setIsFetchingPdfBlob(false);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setIsFetchingPdfBlob(true);
    fetch(pdfUrl)
      .then((res) => res.arrayBuffer())
      .then((data) => {
        if (cancelled) return;
        const blob = new Blob([data], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(blob);
        setBlobPdfUrl(objectUrl);
        setIsFetchingPdfBlob(false);
        onPdfBuiltRef.current?.();
      })
      .catch(() => {
        if (!cancelled) {
          setIsFetchingPdfBlob(false);
          toast({
            title: 'Preview failed',
            description: 'Could not load PDF preview. You can still download the PDF.',
            variant: 'destructive',
          });
          onPdfBuiltRef.current?.();
        }
      });
    return () => {
      cancelled = true;
      setIsFetchingPdfBlob(false);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pdfUrl]);

  const ensureReportExists = async (): Promise<number> => {
    if (effectiveReportId && effectiveReportId > 0) return effectiveReportId;
    const created = await OpenAIClientService.CreateAiResponseReport?.(
      AiResponseReport.create({
        text: aiResponse.response ?? '',
        aiResponsesId,
      }),
    );
    if (!created?.id) throw new Error('Failed to create report');
    setReportId(created.id);
    queryClient.invalidateQueries({
      queryKey: [queryKeys.aiResponseReport.root, 'batch', aiResponsesId],
    });
    return created.id;
  };

  const handleStep1Next = async () => {
    setIsProcessingStep1(true);
    try {
      let rid = effectiveReportId ?? null;
      if (!rid) {
        rid = await ensureReportExists();
      }
      if (rid && files.length > 0) {
        const updates = getFilesToUpdate(files, selectedFileIds, rid);
        if (updates.length > 0) {
          await batchUpdateReportFilesMutation.mutateAsync(
            BatchUpdateAiResponseReportFilesRequest.create({ updates }),
          );
        }
      }
      setStep(2);
    } catch {
      toast({
        title: 'Could not proceed',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessingStep1(false);
    }
  };

  const handleToggleFile = (file: AiResponseReportFileWithUrl) => {
    const id = file.id;
    if (!id) return;
    setSelectedFileIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((f) => f !== id);
      }
      return [...prev, id];
    });
  };

  const handleDeselectAll = () => {
    setSelectedFileIds([]);
  };

  const handlePersistAndGoToStep3 = async () => {
    if (jobNumberOptions.length > 0 && selectedJobNumbers.length === 0) {
      setJobNumberFieldError('Please select at least one job number.');
      requestAnimationFrame(() => {
        jobNumbersInputRef.current?.focus();
      });
      return;
    }

    let rid = effectiveReportId ?? null;
    if (!rid) {
      try {
        rid = await ensureReportExists();
      } catch {
        toast({
          title: 'No report',
          description: 'Report is not available. Please try again.',
          variant: 'destructive',
        });
        return;
      }
    }

    setPdfGenerationError(null);
    setIsDownloadCooldown(false);
    setBlobPdfUrl(null);
    setPdfUrl(null);
    setStep(3);
    setIsGeneratingPdf(true);

    try {
      await updateReportMutation.mutateAsync(
        AiResponseReport.create({ id: rid, text: editedText, fieldMask: ['text'] }),
      );

      const urlObj = await OpenAIClientService.DownloadAiResponseReport?.(
        AiResponseReport.create({
          id: rid,
          jobNumbers: selectedJobNumbers.length > 0 ? selectedJobNumbers.join(', ') : '',
        }),
      );
      if (urlObj?.url) {
        setPdfUrl(urlObj.url);
        setPdfGenerationError(null);
      } else {
        throw new Error('No URL returned');
      }
    } catch {
      setPdfGenerationError('Could not generate PDF. Please try again.');
      toast({
        title: 'PDF generation failed',
        description: 'Could not generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!effectiveReportId) return;
    const filename = getPdfDownloadFilename();
    try {
      const urlObj = await OpenAIClientService.DownloadAiResponseReport?.(
        AiResponseReport.create({
          id: effectiveReportId,
          jobNumbers: selectedJobNumbers.length > 0 ? selectedJobNumbers.join(', ') : '',
        }),
      );
      if (urlObj?.url) {
        const a = document.createElement('a');
        a.href = urlObj.url;
        a.download = filename;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      }
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download PDF. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep(1);
    setSelectedFileIds([]);
    setSelectedJobNumbers([]);
    hasPreselectedJobNumber.current = false;
    setJobNumberFieldError(null);
    hasInitializedFromDbRef.current = false;
    setReportTextHydratedForId(null);
    setEditedText('');
    setPdfUrl(null);
    setBlobPdfUrl(null);
    setReportId(null);
    setTextSaveStatus('idle');
    setIsGeneratingPdf(false);
    setIsFetchingPdfBlob(false);
    setPdfGenerationError(null);
    setIsProcessingStep1(false);
    lastDownloadTimeRef.current = 0;
    setIsDownloadCooldown(false);
  };

  useEffect(() => {
    if (!isDownloadCooldown) return;
    const id = setTimeout(() => setIsDownloadCooldown(false), DOWNLOAD_COOLDOWN_MS);
    return () => clearTimeout(id);
  }, [isDownloadCooldown]);

  useEffect(() => {
    if (selectedJobNumbers.length > 0) {
      setJobNumberFieldError(null);
    }
  }, [selectedJobNumbers.length]);

  useEffect(() => {
    if (step !== 2) {
      setJobNumberFieldError(null);
    }
  }, [step]);
  const isLoading =
    batchGetQuery.isLoading ||
    (reportDataQuery.isLoading && !!effectiveReportId) ||
    reportDataQuery.isFetching;

  const selectedCount = selectedFileIds.length;

  // Phones: full-width footer buttons, 44px on touch; desktop keeps its compact ones.
  const footerButtonSize = isMobileDevice ? 'default' : 'sm';
  const footerButtonClass = isMobileDevice ? 'touch:min-h-11 w-full' : 'min-w-[140px]';

  /** Server PDF generation or client blob load — download stays disabled until both finish */
  const isPdfPipelineBusy = isGeneratingPdf || isFetchingPdfBlob;

  const content = (
    <div
      className={`flex w-full flex-col overflow-hidden ${isMobileDevice ? 'h-dvh' : 'max-h-[90vh]'}`}
    >
      <DialogHeader
        className={
          isMobileDevice
            ? 'bg-background shrink-0 border-b px-4 py-3 text-left'
            : 'sticky top-0 z-10 shrink-0 border-b bg-white px-6 py-4'
        }
      >
        {isMobileDevice ? (
          <div className="flex items-center justify-between gap-2">
            <DialogTitle>Build PDF</DialogTitle>
            <Button variant="ghost" className="-mr-2" onClick={handleClose}>
              Close
            </Button>
          </div>
        ) : (
          <DialogTitle>Build PDF</DialogTitle>
        )}
        <div
          className={`flex flex-wrap items-center gap-2 text-sm ${
            isMobileDevice ? 'text-muted-foreground' : 'mt-2 text-gray-600'
          }`}
        >
          <span>
            Step {step} of 3:{' '}
            {step === 1 ? 'Photos' : step === 2 ? 'Review Report' : 'Download PDF'}
          </span>
          <span>•</span>
          <span>{selectedCount} photo(s) selected</span>
        </div>
      </DialogHeader>

      <div className={`flex-1 overflow-y-auto py-4 ${isMobileDevice ? 'px-4' : 'p-6'}`}>
        {isLoading && !reportDataQuery.data ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <LoadingIcon className="h-10 w-10" />
          </div>
        ) : step === 1 ? (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Select Photos</h3>
            <p className="!mt-1 text-sm text-gray-600">
              Tap photos in the order you want them in the PDF. First tap = first in PDF.
            </p>
            {files.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                <p className="text-sm text-gray-500">No photos available</p>
                <p className="mt-2 text-sm text-gray-600">
                  You can proceed to the next step to add report text only.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {files.map((file) => (
                  <PdfPhotoTile
                    key={file.id}
                    file={file}
                    order={selectedFileIds.indexOf(file.id ?? 0) + 1}
                    onToggle={() => handleToggleFile(file)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : step === 2 ? (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Review & Edit Report Summary</h3>
            <p className="!mt-1 text-sm text-gray-600">
              Review and edit the report text. Changes are saved automatically.
            </p>
            {(isJobNumbersPipelineLoading || jobNumberOptions.length > 0) && (
              <div className="space-y-2">
                <label
                  htmlFor="job-numbers-select"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Job Number(s)
                  {!isJobNumbersPipelineLoading && jobNumberOptions.length > 0 ? (
                    <span className="text-destructive" aria-hidden>
                      {' '}
                      *
                    </span>
                  ) : null}
                </label>
                {isJobNumbersPipelineLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full rounded-md" />
                    <p className="text-sm text-gray-500">Loading job numbers…</p>
                  </div>
                ) : (
                  <>
                    <MultipleSelect
                      ref={jobNumbersInputRef}
                      id="job-numbers-select"
                      placeholder="Select job numbers"
                      selected={selectedJobNumbers}
                      onChange={(values) => {
                        const uniqueValues = [...new Set(values)];
                        setSelectedJobNumbers(uniqueValues);
                      }}
                      disabled={isLoading || isJobNumbersPipelineLoading}
                      options={jobNumberOptions}
                      // One preselected job needs no Clear all; its own × removes it.
                      clearAllMinSelections={2}
                      error={!!jobNumberFieldError}
                      aria-required
                      aria-describedby={jobNumberFieldError ? jobNumbersErrorId : undefined}
                    />
                    {jobNumberFieldError ? (
                      <p
                        id={jobNumbersErrorId}
                        role="alert"
                        className="text-destructive text-[0.8rem] font-medium"
                      >
                        {jobNumberFieldError}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            )}
            <div className="relative">
              {!isStep2ReportTextReady ? (
                <div className="space-y-2">
                  <Skeleton className="min-h-[250px] w-full rounded-md md:min-h-[350px]" />
                  <p className="text-sm text-gray-500">Loading saved report…</p>
                </div>
              ) : (
                <Textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="min-h-[250px] font-sans text-sm md:min-h-[350px]"
                  placeholder="Report text..."
                />
              )}
              {isStep2ReportTextReady ? (
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditedText(aiResponse.response ?? '')}
                  >
                    Use original report
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        ) : step === 3 ? (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">PDF Preview</h3>
            <p className="!mt-1 text-sm text-gray-600">
              Review the final report. Download when ready.
            </p>
            {pdfGenerationError ? (
              <div className="flex min-h-[300px] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-6">
                <p className="text-sm font-medium text-red-800">PDF generation failed</p>
                <p className="text-sm text-red-600">{pdfGenerationError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPdfGenerationError(null);
                    handlePersistAndGoToStep3();
                  }}
                >
                  Try again
                </Button>
              </div>
            ) : isPdfPipelineBusy ? (
              <div className="relative">
                <Skeleton className="h-[400px] w-full animate-pulse rounded-lg" />
                <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-gray-600">
                  <LoadingIcon className="h-4 w-4" />
                  {isGeneratingPdf ? 'Generating PDF...' : 'Loading PDF preview...'}
                </div>
              </div>
            ) : blobPdfUrl && isMobileDevice ? (
              // Phone browsers don't reliably show a PDF inline; the download opens it instead.
              <div className="border-border flex flex-col gap-1 rounded-md border p-4">
                <p className="text-sm font-semibold">Your PDF is ready</p>
                <p className="text-muted-foreground break-all text-sm">
                  {getPdfDownloadFilename()}
                </p>
                <p className="text-muted-foreground text-sm">
                  {selectedCount} photo{selectedCount === 1 ? '' : 's'} and the report text.
                </p>
              </div>
            ) : blobPdfUrl ? (
              <iframe
                src={`${blobPdfUrl}#toolbar=0&navpanes=0`}
                title="PDF Preview"
                className="h-[400px] w-full rounded-lg border"
              />
            ) : (
              <div className="flex min-h-[200px] items-center justify-center">
                <p className="text-sm text-gray-500">No PDF to preview</p>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div
        className={
          isMobileDevice
            ? 'bg-background shrink-0 border-t px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3'
            : 'sticky bottom-0 shrink-0 border-t bg-white px-6 py-4'
        }
      >
        <div
          className={`flex flex-wrap items-center gap-2 ${
            step === 2 ? 'justify-between' : 'justify-end'
          }`}
        >
          {step === 2 ? (
            <div
              className="min-h-5 text-sm text-gray-500"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {isStep2ReportTextReady && textSaveStatus === 'saving' ? (
                <span className="flex items-center gap-1">
                  <LoadingIcon className="h-4 w-4 shrink-0" />
                  Saving...
                </span>
              ) : null}
              {isStep2ReportTextReady && textSaveStatus === 'saved' ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckIcon className="h-4 w-4 shrink-0" />
                  Saved
                </span>
              ) : null}
            </div>
          ) : null}
          <div
            className={
              isMobileDevice
                ? 'grid w-full grid-cols-2 gap-2'
                : 'flex flex-wrap items-center justify-end gap-2'
            }
          >
            {step === 1 && (
              <>
                <Button
                  variant="outline"
                  size={footerButtonSize}
                  className={footerButtonClass}
                  onClick={handleDeselectAll}
                  disabled={
                    selectedFileIds.length === 0 ||
                    isProcessingStep1 ||
                    isLoading ||
                    batchUpdateReportFilesMutation.isPending
                  }
                >
                  {isMobileDevice ? 'Clear' : 'Deselect all'}
                </Button>
                <Button
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={handleStep1Next}
                  disabled={
                    isProcessingStep1 || isLoading || batchUpdateReportFilesMutation.isPending
                  }
                >
                  {isMobileDevice ? 'Next' : 'Next: Review Report'}
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button
                  variant="outline"
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={() => setStep(1)}
                  disabled={isPdfPipelineBusy}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  {isMobileDevice ? 'Back' : 'Back: Photos'}
                </Button>
                <Button
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={handlePersistAndGoToStep3}
                  disabled={isPdfPipelineBusy || isJobNumbersPipelineLoading}
                  isLoading={isPdfPipelineBusy}
                >
                  {isMobileDevice ? 'Next' : 'Next: Download PDF'}
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </>
            )}
            {step === 3 && (
              <>
                <Button
                  variant="outline"
                  size={footerButtonSize}
                  className={`gap-2 ${footerButtonClass}`}
                  onClick={() => setStep(2)}
                  disabled={isPdfPipelineBusy}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  {isMobileDevice ? 'Back' : 'Back: Review Report'}
                </Button>
                {isPdfPipelineBusy ? (
                  <Button
                    variant="outline"
                    size={footerButtonSize}
                    disabled
                    className={`gap-2 ${footerButtonClass}`}
                  >
                    <LoadingIcon className="h-4 w-4" />
                    Preparing PDF…
                  </Button>
                ) : blobPdfUrl ? (
                  isDownloadCooldown ? (
                    <Button
                      variant="muted"
                      size={footerButtonSize}
                      disabled
                      className={`gap-2 ${footerButtonClass}`}
                    >
                      <CheckIcon className="h-4 w-4" />
                      Downloaded
                    </Button>
                  ) : (
                    <Button
                      size={footerButtonSize}
                      className={`gap-2 ${footerButtonClass}`}
                      onClick={() => {
                        if (!blobPdfUrl) return;
                        const filename = getPdfDownloadFilename();
                        const a = document.createElement('a');
                        a.href = blobPdfUrl;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        lastDownloadTimeRef.current = Date.now();
                        setIsDownloadCooldown(true);
                        toast({
                          title: 'PDF Downloaded',
                          description: 'Check your downloads folder.',
                          variant: 'success',
                        });
                      }}
                    >
                      <DownloadIcon className="h-4 w-4" />
                      Download PDF
                    </Button>
                  )
                ) : (
                  <Button
                    size={footerButtonSize}
                    className={`gap-2 ${footerButtonClass}`}
                    onClick={handleDownloadPdf}
                    disabled={!pdfUrl || isPdfPipelineBusy}
                  >
                    <DownloadIcon className="h-4 w-4" />
                    Download PDF
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent
        className={
          isMobileDevice
            ? 'bg-background left-0 top-0 h-dvh w-full max-w-none translate-x-0 translate-y-0 overflow-hidden rounded-none border-0 p-0 sm:rounded-none'
            : 'max-h-[90vh] max-w-2xl max-w-[800px] overflow-hidden bg-white p-0'
        }
        showClose={!isMobileDevice}
        // Full screen on phones: it must not re-center itself for the keyboard.
        keepFocusVisibleAboveKeyboard={!isMobileDevice}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
};
